/**
 * Botón "Recalcular finanzas (masivo)" — versión GLOBAL del recálculo, visible
 * solo a admin/superadmin. Complementa al `RecalcBookingButton` (por-reserva):
 * en vez de una reserva, reprocesa en lote todos los bookings de Stays cuyo
 * ingreso cae en un rango de fechas.
 *
 * Aplica EXACTAMENTE las mismas guardas que el botón por-reserva (D3, round
 * 2026-06-20): preserva las reservas inmutables (cierre de mes), preserva las
 * que tienen ediciones manuales financieras salvo que se marque "Forzar", y
 * preserva los aseos ya PAGADOS (no se re-derivan). Tras correr muestra un
 * resumen explícito por categoría (recalculadas / omitidas / errores) — sin
 * truncado silencioso.
 *
 * Llama a `POST /api/stays/recalc-bookings` vía `staysRecalcApi`. Se usa en el
 * toolbar de /arriendos.
 */
import { useState } from "react";
import {
  Button,
  Checkbox,
  Space,
  Typography,
  Tooltip,
  Modal,
  DatePicker,
  Descriptions,
  Alert,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import type { Dayjs } from "dayjs";
import { staysRecalcApi, type RecalcBulkResponse } from "../services/staysRecalc.api";
import { getMsg } from "../utils/antdStatic";
import { isAdminRole } from "../utils/roles.utils";

const { Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

const fmtCLP = (n: number | null | undefined): string => {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(Number(n));
};

const fmtDeltaSigned = (n: number): string => {
  const sign = n > 0 ? "+" : "";
  return `${sign}${fmtCLP(n)}`;
};

interface RecalcBookingsBulkButtonProps {
  /** Rol del usuario actual — el botón sólo se renderiza si es admin. */
  userRole: string | string[] | null | undefined;
  /**
   * Rango por defecto para prefilar el RangePicker del modal (típicamente el
   * período que el admin está viendo en la página). El admin puede cambiarlo
   * dentro del modal — el scope es explícito, no heredado en silencio.
   */
  defaultRange?: [Dayjs, Dayjs] | null;
  /** Callback tras recalc masivo exitoso (para refrescar la tabla). */
  onRecalculated?: () => void;
  /** Tamaño del botón AntD. */
  size?: "small" | "middle" | "large";
}

export default function RecalcBookingsBulkButton({
  userRole,
  defaultRange,
  onRecalculated,
  size = "middle",
}: RecalcBookingsBulkButtonProps) {
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(defaultRange ?? null);
  const [useCurrentTasa, setUseCurrentTasa] = useState(false);
  const [force, setForce] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RecalcBulkResponse | null>(null);

  // Solo admin/superadmin ven este botón. Cliente y aseador no.
  if (!isAdminRole(userRole)) return null;

  const openModal = () => {
    setResult(null);
    setRange(defaultRange ?? null);
    setOpen(true);
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const resp = await staysRecalcApi.recalcBookingsBulk({
        from: range?.[0]?.format("YYYY-MM-DD"),
        to: range?.[1]?.format("YYYY-MM-DD"),
        useCurrentTasaComision: useCurrentTasa,
        force,
      });
      if (!resp) {
        // apiFetch ya mostró el toast de error (incl. 400 por rango muy grande).
        setLoading(false);
        return;
      }
      // Mostramos el resumen DENTRO del modal para que el admin lo lea con
      // calma; el toast es solo un resumen corto.
      setResult(resp);
      const s = resp.summary;
      getMsg()?.success(
        `Recálculo masivo: ${s.recalculados} recalculadas de ${s.total}.`,
        6,
      );
      onRecalculated?.();
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    if (loading) return;
    setOpen(false);
    setResult(null);
    setForce(false);
  };

  const tooltipTitle = (
    <span>
      Reprocesa en lote el último JSON de Stays de todas las reservas del rango
      elegido y vuelve a calcular sus campos financieros + re-sincroniza aseo y
      lavandería. Preserva reservas inmutables, ediciones manuales (salvo
      "Forzar") y aseos ya pagados. No crea ni elimina reservas.
    </span>
  );

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <Button size={size} icon={<ReloadOutlined />} onClick={openModal}>
          Recalcular finanzas (masivo)
        </Button>
      </Tooltip>

      <Modal
        open={open}
        title={
          <Space>
            <ReloadOutlined />
            <span>Recalcular finanzas en lote</span>
          </Space>
        }
        okText={
          result ? "Cerrar" : loading ? "Recalculando…" : "Sí, recalcular en lote"
        }
        cancelText="Cancelar"
        confirmLoading={loading}
        onOk={result ? closeModal : handleConfirm}
        onCancel={closeModal}
        // Cuando ya hay resultado, ocultamos "Cancelar" (solo queda "Cerrar").
        cancelButtonProps={{ style: result ? { display: "none" } : undefined }}
        maskClosable={!loading}
        width={640}
      >
        {result ? (
          // ── Vista de RESUMEN tras correr ─────────────────────────────────
          <div>
            <Paragraph>{result.message}</Paragraph>
            <Descriptions
              bordered
              size="small"
              column={1}
              style={{ marginBottom: 12 }}
            >
              <Descriptions.Item label="Reservas en el rango">
                {result.summary.total}
              </Descriptions.Item>
              <Descriptions.Item label="Recalculadas">
                {result.summary.recalculados}
              </Descriptions.Item>
              <Descriptions.Item label="Sin cambios (ya al día)">
                {result.summary.sinCambio}
              </Descriptions.Item>
              <Descriptions.Item label="Omitidas — inmutables (cierre de mes)">
                {result.summary.omitidosInmutables}
              </Descriptions.Item>
              <Descriptions.Item label="Omitidas — edición manual">
                {result.summary.omitidosEdicionManual}
              </Descriptions.Item>
              <Descriptions.Item label="Sin snapshot de Stays">
                {result.summary.sinPayload}
              </Descriptions.Item>
              <Descriptions.Item label="Errores (revisar logs)">
                {result.summary.errores}
              </Descriptions.Item>
              <Descriptions.Item label="Δ Ingreso AF total">
                {fmtDeltaSigned(result.deltaIngresoAfTotal)}
              </Descriptions.Item>
              <Descriptions.Item label="Δ Ingreso propietario total">
                {fmtDeltaSigned(result.deltaClienteTotal)}
              </Descriptions.Item>
              <Descriptions.Item label="Propiedades afectadas">
                {result.propiedadesAfectadas}
              </Descriptions.Item>
            </Descriptions>
            {result.summary.omitidosEdicionManual > 0 && !result.force && (
              <Alert
                type="info"
                showIcon
                message="Reservas con edición manual omitidas"
                description={
                  "Se omitieron reservas con ediciones manuales (extensiones, " +
                  "otros cargos, salida tardía con monto, override de subitems). " +
                  'Si quieres recalcularlas pisando esos valores, vuelve a correr ' +
                  'con "Forzar" marcado.'
                }
              />
            )}
          </div>
        ) : (
          // ── Vista de CONFIRMACIÓN antes de correr ───────────────────────
          <div>
            <Paragraph>
              Esta acción reprocesa el <Text strong>último JSON</Text> de Stays
              de <Text strong>todas las reservas</Text> cuyo check-in cae en el
              rango elegido, y recalcula sus valores financieros (precio noches,
              comisiones, IVA, ingreso AF, ingreso propietario, limpieza) más el
              aseo y la lavandería asociados.
            </Paragraph>

            <Paragraph type="secondary" style={{ fontSize: 12, marginTop: -4 }}>
              ℹ Mismas reglas que el botón "Recalcular finanzas" de cada
              reserva, aplicadas en lote.
            </Paragraph>

            <div style={{ marginBottom: 12 }}>
              <Text strong>Rango de fechas (por check-in)</Text>
              <br />
              <RangePicker
                style={{ width: "100%", marginTop: 4 }}
                format="DD/MM/YYYY"
                value={range ?? undefined}
                onChange={(v) =>
                  setRange(v && v[0] && v[1] ? [v[0], v[1]] : null)
                }
                disabled={loading}
                allowClear
              />
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Si lo dejas vacío recalcula TODAS las reservas de Stays (con un
                tope de 1000 por corrida — si lo supera, achica el rango).
              </Text>
            </div>

            <div
              style={{
                background: "#f6ffed",
                border: "1px solid #b7eb8f",
                borderRadius: 4,
                padding: "10px 12px",
                marginBottom: 12,
              }}
            >
              <Paragraph style={{ marginBottom: 4 }}>
                <Text strong>Qué se preserva (no se pisa):</Text>
              </Paragraph>
              <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
                <li>Reservas inmutables (cierre de mes ya cerrado).</li>
                <li>
                  Reservas con edición manual financiera — salvo que marques
                  "Forzar".
                </li>
                <li>Aseos ya pagados (no se re-derivan nunca).</li>
              </ul>
            </div>

            <Paragraph type="secondary" style={{ marginBottom: 12 }}>
              <Text strong>NO modifica</Text> fechas, huésped, propiedad. No crea
              ni elimina reservas — solo recalcula columnas financieras y
              re-sincroniza los satélites (aseo / lavandería).
            </Paragraph>

            <Space direction="vertical" style={{ width: "100%" }}>
              <Checkbox
                checked={useCurrentTasa}
                onChange={(e) => setUseCurrentTasa(e.target.checked)}
                disabled={loading}
              >
                <Text>
                  Actualizar también la tasa de comisión a la actual de cada
                  propiedad.
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Por defecto se conserva la tasa histórica de cada arriendo
                  (regla de "snapshot inmutable").
                </Text>
              </Checkbox>

              <Checkbox
                checked={force}
                onChange={(e) => setForce(e.target.checked)}
                disabled={loading}
              >
                <Text>
                  Forzar: recalcular incluso reservas con ediciones manuales
                  (pisa esos valores).
                </Text>
              </Checkbox>
            </Space>
          </div>
        )}
      </Modal>
    </>
  );
}
